import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, RefreshCw, X } from 'lucide-react';
import '../css/CarePlan.css';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, HeadingLevel, WidthType, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import NavigationBar from '../components/NavigationBar';
import PatientTabs from '../components/PatientTabs';
import usePatientSummary from '../utils/usePatientSummary';
import { fetchPatientBasics } from '../utils/patientsApi';


const formatCreatedAt = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const CarePlan = ({ user: propUser, onClose }) => {
    // 이 화면은 두 가지로 열린다.
    //   · 대시보드 모달  → user 객체를 통째로 받는다
    //   · 탭/주소       → /careplan/:userId 에서 id 만 받는다
    // 아래 두 필드 말고는 user 에서 쓰는 것이 없어서, 라우트로 올 때는 id 만으로 충분하다.
    const { userId: paramUserId } = useParams();
    const navigate = useNavigate();
    const appUserId = propUser?.app_user_id ?? propUser?.user_id ?? paramUserId;
    // 케어플랜 응답에 이름이 없을 때의 머리말과, 스마트링 탭 표시에 쓴다.
    const summary = usePatientSummary(appUserId);
    const isModal = typeof onClose === 'function';
    const handleClose = isModal ? onClose : () => navigate(-1);

    // API에서 받은 데이터를 이미지 구조에 맞게 매핑
    // 대상자 정보의 출처는 둘이다. 예전에는 대시보드 모달이 user 객체를 통째로 넘겼고,
    // 지금은 주소(/careplan/:userId)로만 열리므로 id 로 직접 받아 온다.
    const [fetchedSubject, setFetchedSubject] = useState(null);
    const subject = propUser || fetchedSubject;
    const data = subject || {};

    // CarePlan 결과 저장 상태
    const [carePlanResult, setCarePlanResult] = useState(null);
    const [guardianPlanResult, setGuardianPlanResult] = useState(null);
    const [activeTab, setActiveTab] = useState('expert'); // 'expert' or 'guardian'
    const [createdAt, setCreatedAt] = useState(null); // 생성일시 저장
    const [isConfirmOpen, setIsConfirmOpen] = useState(false); // 커스텀 확인창 상태
    // 라우트로 들어와 저장된 계획이 없는 상태. 로딩 문구 대신 안내를 그린다.
    const [noSavedPlan, setNoSavedPlan] = useState(false);
    // 생성 실패(수치 검증 실패 포함). 로딩 문구 대신 사유를 그린다.
    const [generateError, setGenerateError] = useState(null);

    const isFetched = useRef(false);

    // 컴포넌트 마운트 시 CarePlan 데이터 조회
    useEffect(() => {
        // 대상자를 못 집으면 부를 주소가 없다 — /api/careplan/undefined/... 를 쏘지 않는다.
        // 그 경우 화면은 아래에서 "대상자를 찾을 수 없습니다"로 떨어진다.
        if (isFetched.current || !appUserId) return;
        isFetched.current = true;

        const loadCarePlan = async () => {
            const currentUserId = appUserId;

            // 주소로 열리면 id 밖에 없다. 생성에는 나이와 성별이 필요하고 그 둘은
            // 측정값 응답에만 실려 오므로 여기서 먼저 받는다. 없으면 생성 버튼을
            // 그리지 않는다 — 나이 0·성별 미상으로 만든 조언은 임상 근거가 틀린다.
            if (!propUser) {
                try {
                    const basics = await fetchPatientBasics({
                        token: localStorage.getItem('token'),
                        appUserId: currentUserId
                    });
                    if (basics) setFetchedSubject(basics);
                } catch (error) {
                    console.error('[CarePlan] 대상자 정보 조회 오류:', error);
                }
            }

            // 0. DB에서 최근 케어플랜 조회
            try {
                const latestResponse = await axios.get(`/api/careplan/${currentUserId}/latest-plan`);
                if (latestResponse.data && latestResponse.data.success && latestResponse.data.data) {
                    const dbData = latestResponse.data.data;
                    console.log('[CarePlan] DB에서 케어플랜 불러오기 완료:', dbData.created_at);
                    if (dbData.expert_content) setCarePlanResult(dbData.expert_content);
                    if (dbData.guardian_content) setGuardianPlanResult(dbData.guardian_content);
                    if (dbData.created_at) setCreatedAt(dbData.created_at);
                    return; // DB에서 불러왔으면 이후 로직(API 호출) 생략
                }
            } catch (error) {
                console.error('[CarePlan] 최신 케어플랜 DB 조회 오류:', error);
            }

            // 저장된 것이 없으면 알리기만 하고 멈춘다. **생성은 사람이 눌러야 한다.**
            // 열자마자 자동으로 만들면 탭을 누를 때마다 1~2분짜리 생성이 돌고, 그 사이
            // 화면이 "기다려 주세요"를 띄워 재클릭을 부른다.
            setNoSavedPlan(true);
        };

        loadCarePlan();
    }, []);

    // 케어플랜 생성. 입력(측정·설문·병력·위험도)은 서버가 DB 에서 만든다 — 화면은 수치를 보내지 않는다.
    // 예전에는 화면이 최근 3회 평균을 모아 보냈고, 측정이 없으면 0 이 가서 모델이 지침 견본 값을 썼다.
    // 서버가 수치 검증에 두 번 걸리면 저장하지 않고 422 로 사유를 준다. 그 사유를 화면에 그대로 보여 준다.
    const generateCarePlanFromAPI = async (currentUserId) => {
        setCarePlanResult(null);
        setGuardianPlanResult(null);
        setCreatedAt(null);
        setGenerateError(null);
        try {
            // 생성은 인증이 필요하다(서버가 비용을 내는 호출). 이 앱은 요청마다 토큰을 직접 붙인다 — 전역 인터셉터가 없다.
            const token = localStorage.getItem('token');
            const response = await axios.post(`/api/careplan/${currentUserId}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 180000
            });
            const result = response.data || {};
            if (result.expert_plan) setCarePlanResult(result.expert_plan);
            if (result.guardian_plan) setGuardianPlanResult(result.guardian_plan);
            if (result.expert_plan || result.guardian_plan) {
                setCreatedAt(new Date().toISOString());
            } else {
                setGenerateError({ message: '케어플랜 응답이 비어 있습니다.', reasons: [] });
            }
        } catch (error) {
            console.error('[CarePlan] 생성 오류:', error.response?.data || error.message);
            const data = error.response?.data;
            if (error.response?.status === 422 && Array.isArray(data?.reasons)) {
                setGenerateError({ message: data.message, reasons: data.reasons.map((r) => r.message || r.code) });
            } else {
                setGenerateError({ message: data?.message || '케어플랜 생성에 실패했습니다.', reasons: [data?.details || error.message].filter(Boolean) });
            }
        }
    };

    // 재생성 버튼 클릭 시 모달 열기
    const handleRegenerateClick = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsConfirmOpen(true);
    };

    // 처음 만들 때. 되돌릴 것이 없으므로 확인 창 없이 바로 만든다.
    const handleGenerateClick = async () => {
        setNoSavedPlan(false);
        await generateCarePlanFromAPI(appUserId);
    };

    // 모달에서 확인 클릭 시 실제 재생성 로직 수행
    const handleConfirmRegenerate = async () => {
        setIsConfirmOpen(false);
        await generateCarePlanFromAPI(appUserId);
    };



    // DOC 파일로 내보내기 함수
    const exportToDoc = async () => {
        const activeContent = activeTab === 'guardian' && guardianPlanResult ? guardianPlanResult : carePlanResult;
        if (!activeContent) {
            alert('파일이 아직 생성되지 않았습니다.잠시 후 다시 시도해주세요.');
            return;
        }

        try {
            // 마크다운 텍스트를 줄 단위로 분리
            const lines = activeContent.split('\n');
            const children = [];

            let currentTable = null;
            let tableRows = [];

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();

                // 빈 줄 건너뛰기
                if (!line) {
                    // 테이블이 진행 중이었다면 종료
                    if (currentTable) {
                        children.push(createTable(tableRows));
                        currentTable = null;
                        tableRows = [];
                    }
                    continue;
                }

                // 테이블 감지 (| 로 시작)
                if (line.startsWith('|')) {
                    // 양 끝의 | 제거 후 분리하여 빈 셀도 보존
                    const cells = line.split('|')
                        .slice(1, -1)
                        .map(cell => cell.trim());

                    // 구분선 건너뛰기 (---, :---, ---:, :---: 등)
                    if (cells.every(cell => /^[ :-]+$/.test(cell))) {
                        continue;
                    }

                    if (!currentTable) {
                        currentTable = true;
                    }
                    tableRows.push(cells);
                    continue;
                } else if (currentTable) {
                    // 테이블 종료
                    children.push(createTable(tableRows));
                    currentTable = null;
                    tableRows = [];
                }

                // 헤딩 처리
                if (line.startsWith('# ')) {
                    children.push(new Paragraph({
                        children: [new TextRun({ text: line.substring(2), bold: true, size: 32, color: "000000" })],
                        heading: HeadingLevel.HEADING_1
                    }));
                } else if (line.startsWith('## ')) {
                    children.push(new Paragraph({
                        children: [new TextRun({ text: line.substring(3), bold: true, size: 28, color: "000000" })],
                        heading: HeadingLevel.HEADING_2
                    }));
                } else if (line.startsWith('### ')) {
                    children.push(new Paragraph({
                        children: [new TextRun({ text: line.substring(4), bold: true, size: 24, color: "000000" })],
                        heading: HeadingLevel.HEADING_3
                    }));
                } else {
                    // 일반 텍스트 (볼드 처리 포함)
                    const textRuns = parseMarkdownText(line);
                    children.push(new Paragraph({ children: textRuns }));
                }
            }

            // 마지막 테이블 처리
            if (currentTable && tableRows.length > 0) {
                children.push(createTable(tableRows));
            }

            // Word 문서 생성
            const doc = new Document({
                sections: [{
                    children: children
                }]
            });

            // 파일 생성 및 다운로드
            const blob = await Packer.toBlob(doc);
            saveAs(blob, 'CarePlan.docx');
            console.log('DOC 파일 다운로드 완료');
        } catch (error) {
            console.error('DOC 파일 생성 오류:', error);
            alert('DOC 파일 생성 중 오류가 발생했습니다.');
        }
    };

    // 테이블 생성 헬퍼 함수
    const createTable = (rows) => {
        if (rows.length === 0) return null;

        const columnCount = rows[0].length;
        // A4 용지의 일반적인 가공 너비 (twips/DXA 단위: 약 9072는 표준 여백 제외 영역)
        const totalWidth = 9072;
        const cellWidth = Math.floor(totalWidth / columnCount);

        return new Table({
            width: {
                size: totalWidth,
                type: WidthType.DXA,
            },
            columnWidths: Array(columnCount).fill(cellWidth),
            alignment: AlignmentType.LEFT,
            rows: rows.map((cells) =>
                new TableRow({
                    children: cells.map((cellText) =>
                        new TableCell({
                            width: {
                                size: cellWidth,
                                type: WidthType.DXA,
                            },
                            margins: {
                                top: 100,
                                bottom: 100,
                                left: 100,
                                right: 100
                            },
                            children: [new Paragraph({
                                children: parseMarkdownText(cellText),
                                spacing: {
                                    before: 50,
                                    after: 50
                                }
                            })]
                        })
                    )
                })
            )
        });
    };

    // 마크다운 텍스트 파싱 (볼드 등)
    const parseMarkdownText = (text) => {
        const parts = [];
        const regex = /\*\*(.*?)\*\*/g;
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
            // 볼드 앞의 일반 텍스트
            if (match.index > lastIndex) {
                parts.push(new TextRun({ text: text.substring(lastIndex, match.index), color: "000000" }));
            }
            // 볼드 텍스트
            parts.push(new TextRun({ text: match[1], bold: true, color: "000000" }));
            lastIndex = regex.lastIndex;
        }

        // 남은 일반 텍스트
        if (lastIndex < text.length) {
            parts.push(new TextRun({ text: text.substring(lastIndex), color: "000000" }));
        }

        return parts.length > 0 ? parts : [new TextRun({ text: text, color: "000000" })];
    };


    // 대상자를 못 집으면 아무것도 조회할 수 없다. 모달은 user 객체를, 라우트는
    // :userId 를 준다 — 둘 다 없다는 것은 잘못된 주소로 들어왔다는 뜻이다.
    if (!appUserId) {
        return (
            // 아래 본문과 같은 껍데기다 — 모달로 열렸으면 --modal 을 붙여야 대화상자 안에
            // 100vh 와 페이지 바탕색이 적용되지 않는다.
            <div className={`careplan-page${isModal ? ' careplan-page--modal' : ''}`}>
                <div className="sds-page detail-page">
                    <div className="sds-notice sds-notice--error">대상자를 찾을 수 없습니다.</div>
                </div>
            </div>
        );
    }

    const hasPlan = Boolean(carePlanResult || guardianPlanResult);
    const planText = activeTab === 'expert' ? carePlanResult : guardianPlanResult;

    return (
        <div className={`careplan-page${isModal ? ' careplan-page--modal' : ''}`}>
            {/* ⚠️ 상단 바는 .sds-page 의 바로 앞 형제여야 한다. NavigationBar.css 153행의
                `.navigation-bar + *` 가 아래 내용의 위 여백(64+24)을 주는데, 감싸는 요소를
                하나라도 끼우면 인접 선택자가 끊긴다(Survey.js 가 그래서 여백을 손으로 맞춘다).
                모달로 열리면 대시보드 안이라 그리지 않는다 — 그때는 이 규칙도 걸리지 않는다. */}
            {!isModal && <NavigationBar />}
            {/* 다른 대상자 화면과 같은 껍데기다 — 회색 바탕 위 .sds-page 폭, sds-page-header,
                탭 줄, 그 아래 흰 카드. 모달로 열리면 대시보드가 이미 흰 상자를 씌우므로
                바탕색과 바깥 여백만 careplan-page--modal 로 걷는다. */}
            <div className="sds-page detail-page">
                {/* 뒤로가기는 헤더 오른쪽이 아니라 브레드크럼 자리다 — 개요·스마트링 화면과 같다.
                    모달에는 돌아갈 히스토리가 없으므로 이 줄 대신 헤더의 닫기 버튼을 쓴다. */}
                {!isModal && (
                    <div className="detail-page__crumb">
                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={handleClose}>
                            <ChevronLeft size={16} aria-hidden="true" />뒤로
                        </button>
                    </div>
                )}

                <div className="sds-page-header">
                    <div>
                        {/* 제목은 대상자다. 어느 화면인지는 아래 탭 줄이 말한다. 라우트로 오면
                            이름을 모른다 — 그때는 id 로 부른다(다른 상세 화면과 같은 규칙). */}
                        <h1 className="sds-page-header__title">
                            {data.name || summary?.name || `대상자 ${appUserId}`}
                            <small>개인 맞춤 만성질환 케어플랜</small>
                        </h1>
                    </div>
                    <div className="sds-page-header__actions">
                        {createdAt && (
                            <span className="sds-badge sds-badge--none">생성 {formatCreatedAt(createdAt)}</span>
                        )}
                        {/* 나이·성별을 아는 경우에만 보인다. 그 둘 없이 만든 조언은
                            임상 근거가 틀린다. */}
                        {subject && hasPlan && (
                            <button className="sds-btn sds-btn--secondary" onClick={handleRegenerateClick}>
                                <RefreshCw size={16} aria-hidden="true" />최근 데이터로 재생성
                            </button>
                        )}
                        {hasPlan && (
                            <button className="sds-btn sds-btn--primary" onClick={exportToDoc}>
                                <Download size={16} aria-hidden="true" />워드 파일 다운로드
                            </button>
                        )}
                        {isModal && (
                            <button className="sds-btn sds-btn--ghost" onClick={handleClose}>
                                <X size={16} aria-hidden="true" />닫기
                            </button>
                        )}
                    </div>
                </div>

                {/* 모달 안에서는 탭 줄을 그리지 않는다. 모달이 닫히지 않은 채 주소만 바뀐다. */}
                {!isModal && <PatientTabs appUserId={appUserId} active="careplan" hasSmartRing={summary?.hasSmartRing === true} />}

                <section className="careplan-page__card">
                    {hasPlan ? (
                        <>
                            {/* 대상 독자 고르기. 보호자용이 없으면 고를 것이 없어 줄 자체를 빼고
                                의사용만 그린다 — 한 칸짜리 세그먼트는 누를 수 있어 보이기만 한다. */}
                            {guardianPlanResult && (
                                <div className="sds-seg careplan-page__audience">
                                    <button
                                        className={`sds-seg__item${activeTab === 'expert' ? ' is-active' : ''}`}
                                        aria-current={activeTab === 'expert' ? 'true' : undefined}
                                        onClick={() => setActiveTab('expert')}
                                    >
                                        의사용 케어플랜
                                    </button>
                                    <button
                                        className={`sds-seg__item${activeTab === 'guardian' ? ' is-active' : ''}`}
                                        aria-current={activeTab === 'guardian' ? 'true' : undefined}
                                        onClick={() => setActiveTab('guardian')}
                                    >
                                        보호자용 케어플랜
                                    </button>
                                </div>
                            )}
                            {planText && (
                                <div className="careplan-page__markdown">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {planText}
                                    </ReactMarkdown>
                                </div>
                            )}
                        </>
                    ) : generateError ? (
                        <div className="careplan-page__empty">
                            <div className="sds-notice sds-notice--error">
                                {generateError.message}
                                {generateError.reasons.length > 0 && (
                                    <ul className="careplan-page__reasons">
                                        {generateError.reasons.map((reason, index) => <li key={index}>{reason}</li>)}
                                    </ul>
                                )}
                            </div>
                            {subject && (
                                <button className="sds-btn sds-btn--primary" onClick={handleGenerateClick}>
                                    <RefreshCw size={16} aria-hidden="true" />다시 생성
                                </button>
                            )}
                        </div>
                    ) : noSavedPlan ? (
                        <div className="careplan-page__empty">
                            <div className="sds-notice">
                                {subject
                                    ? '아직 만들어진 케어플랜이 없습니다. 최근 측정값으로 지금 만들 수 있습니다.'
                                    : '아직 만들어진 케어플랜이 없습니다. 이 대상자의 정보를 불러오지 못해 지금은 만들 수 없습니다.'}
                            </div>
                            {subject && (
                                <button className="sds-btn sds-btn--primary" onClick={handleGenerateClick}>
                                    <RefreshCw size={16} aria-hidden="true" />케어플랜 생성
                                </button>
                            )}
                        </div>
                    ) : (
                        <p className="careplan-page__loading">
                            최적의 케어플랜을 준비하고 있어요!<br />
                            데이터 양에 따라 보고서 생성에 1~2분 정도 걸릴 수 있습니다.<br />
                            페이지를 벗어나지 말고 조금만 더 기다려 주시면 곧 완성됩니다.
                        </p>
                    )}
                </section>
            </div>

            {/* 케어플랜 재생성 확인 모달 */}
            {isConfirmOpen && (
                <div className="sds-modal-overlay">
                    <div className="sds-modal careplan-page__confirm">
                        <div className="sds-modal__head">
                            <h3 className="sds-modal__title">케어플랜 재생성</h3>
                        </div>
                        <div className="sds-modal__body">
                            <p className="careplan-page__confirm-text">
                                최신 데이터로 케어플랜을 다시 생성하시겠습니까?<br />
                                이전 데이터는 최신화됩니다.
                            </p>
                        </div>
                        <div className="sds-modal__foot">
                            <button className="sds-btn sds-btn--secondary" onClick={() => setIsConfirmOpen(false)}>
                                취소
                            </button>
                            <button className="sds-btn sds-btn--primary" onClick={handleConfirmRegenerate}>
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CarePlan; 